import React from 'react';
import { Space, Typography } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@ant-design/icons';

const { Text } = Typography;

type AntIconComponent = React.ComponentType<React.ComponentProps<typeof ToolOutlined>>;

export interface EquipmentDisplayProps {
  equipmentId?: string;
  equipment?: {
    id: string;
    name?: string | null;
    displayId?: string | null;
    code?: string | null;
    icon?: string | null;
  };
  showDisplayId?: boolean;
  showIcon?: boolean;
  noLink?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

/**
 * 設備情報を統一的に表示するコンポーネント
 *
 * @example
 * // 基本的な使用（リンク付き）
 * <EquipmentDisplay equipment={equipment} />
 *
 * @example
 * // displayID付き
 * <EquipmentDisplay equipment={equipment} showDisplayId />
 *
 * @example
 * // リンクなし
 * <EquipmentDisplay equipment={equipment} noLink />
 */
export function EquipmentDisplay({
  equipmentId,
  equipment,
  showDisplayId = false,
  showIcon = true,
  noLink = false,
  size = 'default',
  style
}: EquipmentDisplayProps): JSX.Element | null {
  const navigate = useNavigate();

  // equipmentオブジェクトまたはequipmentIdが必要
  if (!equipment && !equipmentId) {
    return null;
  }

  const effectiveEquipment = equipment || {
    id: equipmentId!,
    name: null,
    displayId: null,
    code: null,
    icon: null
  };

  const displayName =
    effectiveEquipment.name ||
    effectiveEquipment.displayId ||
    effectiveEquipment.code ||
    effectiveEquipment.id;

  const handleClick = () => {
    if (!noLink && effectiveEquipment.id) {
      navigate(`/app/equipments/${effectiveEquipment.id}`);
    }
  };

  // アイコンコンポーネントの取得
  const IconComponent = effectiveEquipment.icon
    ? ((Icons as unknown as Record<string, AntIconComponent>)[effectiveEquipment.icon] ||
        ToolOutlined)
    : ToolOutlined;

  const iconSize = size === 'small' ? 14 : size === 'large' ? 20 : 16;
  const fontSize = size === 'small' ? 12 : size === 'large' ? 16 : 14;

  return (
    <Space size={8} style={{ cursor: noLink ? 'default' : 'pointer', ...style }}>
      {showIcon && <IconComponent style={{ fontSize: iconSize, color: '#1890ff' }} />}
      {noLink ? (
        <Text style={{ fontSize }}>{displayName}</Text>
      ) : (
        <Text onClick={handleClick} style={{ fontSize, cursor: 'pointer' }}>
          {displayName}
        </Text>
      )}
      {showDisplayId && effectiveEquipment.displayId && effectiveEquipment.displayId !== displayName && (
        <Text type="secondary" style={{ fontSize: fontSize - 2 }}>
          ({effectiveEquipment.displayId})
        </Text>
      )}
    </Space>
  );
}
